import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import AuthContext from "../../contexts/AuthProvider";
import { feedbackApi } from "./feedbackApi";
import { getDisplayStatus, STATUS_PRIORITY } from "./feedbackConstants";

const FeedbackContext = createContext(null);

export function useFeedback() {
  const ctx = useContext(FeedbackContext);
  if (!ctx) throw new Error("useFeedback must be used inside FeedbackProvider");
  return ctx;
}

export default function FeedbackProvider({ children }) {
  const { userDetails } = useContext(AuthContext);

  const [topics, setTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [selectedTopicDetail, setSelectedTopicDetail] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [topicsFetched, setTopicsFetched] = useState(false);
  const [filterStatuses, setFilterStatuses] = useState([
    "pending_validation",
    "open",
    "completed",
  ]);
  const [filterCategories, setFilterCategories] = useState([]);
  const [creatingTopic, setCreatingTopic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pendingAiTitleIds, setPendingAiTitleIds] = useState(new Set());
  const savedTimerRef = useRef(null);
  const busyRef = useRef(false);
  const prevImplRef = useRef(userDetails?.implementation_code);

  const currentUser = useMemo(
    () => ({
      id: userDetails?.user_id,
      name: userDetails?.name,
    }),
    [userDetails],
  );

  useEffect(() => {
    const code = userDetails?.implementation_code;
    if (prevImplRef.current && prevImplRef.current !== code) {
      setTopics([]);
      setTopicsFetched(false);
      setSelectedTopicId(null);
      setSelectedTopicDetail(null);
    }
    prevImplRef.current = code;
  }, [userDetails?.implementation_code]);

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await feedbackApi.listTopics();
      const data = res.data;
      setTopics(data);
      setTopicsFetched(true);
      if (data.length > 0) {
        setSelectedTopicId(data[0].id);
      }
    } catch (err) {
      console.error("[Feedback] Failed to fetch topics", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (modalOpen && !topicsFetched) {
      fetchTopics();
    }
  }, [modalOpen, topicsFetched, fetchTopics]);

  const fetchTopicDetail = useCallback(async (id) => {
    setLoadingDetail(true);
    try {
      const res = await feedbackApi.getTopic(id);
      const detail = res.data;
      setSelectedTopicDetail(detail);
      setTopics((prev) => {
        const listTopic = prev.find((t) => t.id === id);
        if (!listTopic) return prev;
        const commentCount = Array.isArray(detail.comments)
          ? detail.comments.length
          : listTopic.comment_count;
        const patch = {
          status: detail.status,
          updated_at: detail.updated_at,
          title: detail.title,
          category: detail.category ?? "",
          comment_count: commentCount,
        };
        const sameCategory = (listTopic.category || "") === patch.category;
        if (
          listTopic.status === patch.status &&
          listTopic.updated_at === patch.updated_at &&
          listTopic.title === patch.title &&
          sameCategory &&
          (listTopic.comment_count || 0) === (patch.comment_count || 0)
        ) {
          return prev;
        }
        return prev.map((t) => (t.id === id ? { ...t, ...patch } : t));
      });
    } catch (err) {
      console.error("[Feedback] Failed to fetch topic detail", err);
      setSelectedTopicDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTopicId) {
      setSelectedTopicDetail(null);
      fetchTopicDetail(selectedTopicId);
    } else {
      setSelectedTopicDetail(null);
    }
  }, [selectedTopicId, fetchTopicDetail]);

  const withSave = useCallback(async (fn) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setSaving(true);
    setSaved(false);
    clearTimeout(savedTimerRef.current);
    try {
      const result = await fn();
      setSaving(false);
      setSaved(true);
      savedTimerRef.current = setTimeout(() => setSaved(false), 1500);
      return result;
    } catch (err) {
      setSaving(false);
      throw err;
    } finally {
      busyRef.current = false;
    }
  }, []);

  const activeTopicsCount = useMemo(() => {
    if (topicsFetched) {
      return topics.filter((t) => {
        const ds = getDisplayStatus(t);
        return ds === "open" || ds === "pending_validation";
      }).length;
    }
    return userDetails?.feedback_active_count || 0;
  }, [topics, topicsFetched, userDetails]);

  const filteredTopics = useMemo(() => {
    const filtered = topics.filter((t) => {
      const ds = getDisplayStatus(t);
      if (filterStatuses.length > 0 && !filterStatuses.includes(ds))
        return false;
      if (filterCategories.length > 0 && !filterCategories.includes(t.category))
        return false;
      return true;
    });
    filtered.sort((a, b) => {
      const pa = STATUS_PRIORITY[getDisplayStatus(a)] ?? 99;
      const pb = STATUS_PRIORITY[getDisplayStatus(b)] ?? 99;
      if (pa !== pb) return pa - pb;
      return new Date(b.updated_at) - new Date(a.updated_at);
    });
    return filtered;
  }, [topics, filterStatuses, filterCategories]);

  const selectedTopic = selectedTopicDetail;

  const toggleFilterStatus = useCallback((status) => {
    setFilterStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  }, []);

  const toggleFilterCategory = useCallback((cat) => {
    setFilterCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    );
  }, []);

  /* ─── CRUD ──────────────────────────────────────────── */

  const addTopic = useCallback(async (contentHtml, files = []) => {
    const res = await feedbackApi.createTopic(contentHtml);
    const newTopic = res.data;

    if (files.length > 0 && newTopic.comments?.length > 0) {
      const cid = newTopic.comments[0].id;
      for (const file of files) {
        try {
          const attRes = await feedbackApi.uploadAttachment(
            newTopic.id,
            cid,
            file,
          );
          newTopic.comments[0].attachments.push(attRes.data);
        } catch (e) {
          console.error("[Feedback] Attachment upload failed", e);
        }
      }
    }

    setTopics((prev) => [
      {
        id: newTopic.id,
        title: newTopic.title,
        category: newTopic.category,
        status: newTopic.status,
        created_by_id: newTopic.created_by_id,
        created_by_name: newTopic.created_by_name,
        created_at: newTopic.created_at,
        updated_at: newTopic.updated_at,
        comment_count: newTopic.comments?.length || 1,
      },
      ...prev,
    ]);
    setSelectedTopicId(newTopic.id);
    setSelectedTopicDetail(newTopic);
    setCreatingTopic(false);

    setPendingAiTitleIds((prev) => new Set([...prev, newTopic.id]));

    const clearPending = (id) =>
      setPendingAiTitleIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

    feedbackApi
      .generateTitle(contentHtml)
      .then((titleRes) => {
        const { title, category } = titleRes.data;
        return feedbackApi
          .updateTopic(newTopic.id, { title, category })
          .then(() => {
            setTopics((prev) =>
              prev.map((t) =>
                t.id === newTopic.id ? { ...t, title, category } : t,
              ),
            );
            setSelectedTopicDetail((prev) =>
              prev?.id === newTopic.id ? { ...prev, title, category } : prev,
            );
            clearPending(newTopic.id);
          });
      })
      .catch((e) => {
        console.error("[Feedback] AI title gen failed", e);
        clearPending(newTopic.id);
      });

    return newTopic;
  }, []);

  const updateTopic = useCallback(
    async (topicId, updates) => {
      await withSave(async () => {
        await feedbackApi.updateTopic(topicId, updates);
        const now = new Date().toISOString();
        setTopics((prev) =>
          prev.map((t) =>
            t.id === topicId ? { ...t, ...updates, updated_at: now } : t,
          ),
        );
        setSelectedTopicDetail((prev) =>
          prev?.id === topicId
            ? { ...prev, ...updates, updated_at: now }
            : prev,
        );
      });
    },
    [withSave],
  );

  const deleteTopic = useCallback(
    async (topicId) => {
      await withSave(async () => {
        await feedbackApi.deleteTopic(topicId);
        setTopics((prev) => prev.filter((t) => t.id !== topicId));
        setSelectedTopicId((prev) => {
          if (prev === topicId) {
            setSelectedTopicDetail(null);
            return null;
          }
          return prev;
        });
      });
    },
    [withSave],
  );

  const addComment = useCallback(
    async (topicId, contentHtml, files = []) => {
      await withSave(async () => {
        const res = await feedbackApi.createComment(topicId, contentHtml);
        const newComment = res.data;

        for (const file of files) {
          try {
            const attRes = await feedbackApi.uploadAttachment(
              topicId,
              newComment.id,
              file,
            );
            newComment.attachments = [
              ...(newComment.attachments || []),
              attRes.data,
            ];
          } catch (e) {
            console.error("[Feedback] Attachment upload failed", e);
          }
        }

        setSelectedTopicDetail((prev) => {
          if (!prev || prev.id !== topicId) return prev;
          return {
            ...prev,
            comments: [...prev.comments, newComment],
            updated_at: newComment.created_at,
          };
        });
        setTopics((prev) =>
          prev.map((t) =>
            t.id === topicId
              ? {
                  ...t,
                  comment_count: (t.comment_count || 0) + 1,
                  updated_at: newComment.created_at,
                }
              : t,
          ),
        );
      });
    },
    [withSave],
  );

  const updateComment = useCallback(
    async (topicId, commentId, newHtml) => {
      await withSave(async () => {
        const res = await feedbackApi.updateComment(
          topicId,
          commentId,
          newHtml,
        );
        const updated = res.data;
        setSelectedTopicDetail((prev) => {
          if (!prev || prev.id !== topicId) return prev;
          return {
            ...prev,
            comments: prev.comments.map((c) =>
              c.id === commentId ? updated : c,
            ),
          };
        });
      });
    },
    [withSave],
  );

  const deleteComment = useCallback(
    async (topicId, commentId) => {
      await withSave(async () => {
        await feedbackApi.deleteComment(topicId, commentId);
        setSelectedTopicDetail((prev) => {
          if (!prev || prev.id !== topicId) return prev;
          return {
            ...prev,
            comments: prev.comments.filter((c) => c.id !== commentId),
          };
        });
        setTopics((prev) =>
          prev.map((t) =>
            t.id === topicId
              ? {
                  ...t,
                  comment_count: Math.max((t.comment_count || 1) - 1, 0),
                }
              : t,
          ),
        );
      });
    },
    [withSave],
  );

  const value = useMemo(
    () => ({
      topics,
      filteredTopics,
      selectedTopic,
      selectedTopicId,
      setSelectedTopicId,
      modalOpen,
      setModalOpen,
      loading,
      loadingDetail,
      filterStatuses,
      toggleFilterStatus,
      filterCategories,
      toggleFilterCategory,
      creatingTopic,
      setCreatingTopic,
      activeTopicsCount,
      currentUser,
      saving,
      saved,
      pendingAiTitleIds,
      addTopic,
      updateTopic,
      deleteTopic,
      addComment,
      updateComment,
      deleteComment,
    }),
    [
      topics,
      filteredTopics,
      selectedTopic,
      selectedTopicId,
      modalOpen,
      loading,
      loadingDetail,
      filterStatuses,
      toggleFilterStatus,
      filterCategories,
      toggleFilterCategory,
      creatingTopic,
      activeTopicsCount,
      currentUser,
      saving,
      saved,
      pendingAiTitleIds,
      addTopic,
      updateTopic,
      deleteTopic,
      addComment,
      updateComment,
      deleteComment,
    ],
  );

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
}

FeedbackProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
